// Type definitions for the Warehouse Optimization System

export interface WarehouseNode {
  node_id: string;
  tenant_id: string;
  name: string;
  location: string;
  capacity?: number;
  active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface CreateNodeRequest {
  name: string;
  location: string;
  capacity?: number;
  active?: boolean;
}

export interface UpdateNodeRequest {
  name?: string;
  location?: string;
  capacity?: number;
  active?: boolean;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface NodesResponse {
  nodes: WarehouseNode[];
  count: number;
  tenant_id: string;
}

export interface NodeResponse {
  message: string;
  node: WarehouseNode;
}

// Inventory and Demand related types
export interface InventoryLevel {
  node_id: string;
  product_id: string;
  tenant_id: string;
  quantity: number;
  last_updated: string;
  updated_by: string;
}

export interface DemandData {
  node_id: string;
  product_id: string;
  tenant_id: string;
  quantity: number;
  forecast_period: string;
  last_updated: string;
}

export interface Product {
  product_id: string;
  name: string;
  unit_of_measure: string;
  category?: string;
}

export interface DemandGap {
  node_id: string;
  product_id: string;
  inventory_quantity: number;
  demand_quantity: number;
  gap: number; // demand - inventory (positive means shortage)
}

export interface InventoryRequest {
  node_id: string;
  product_id: string;
  quantity: number;
}

export interface DemandRequest {
  node_id: string;
  product_id: string;
  quantity: number;
  forecast_period: string;
}

export interface InventoryResponse {
  inventory: InventoryLevel[];
  count: number;
}

export interface DemandResponse {
  demand: DemandData[];
  count: number;
}

export interface DemandGapResponse {
  gaps: DemandGap[];
  total_shortage: number;
  nodes_with_shortage: number;
}

// Purchase Order and Optimization types
export interface PurchaseOrder {
  product_id: string;
  quantity: number;
  allocation_strategy: AllocationStrategy;
}

export enum AllocationStrategy {
  SPLIT = 'SPLIT',
  INTACT = 'INTACT'
}

export interface OptimizationRequest {
  product_id: string;
  purchase_quantity: number;
  allocation_strategy: AllocationStrategy;
  solver_type?: 'simple' | 'advanced';
}

export interface AllocationResult {
  node_id: string;
  allocated_quantity: number;
  before_inventory: number;
  after_inventory: number;
  demand_gap_reduction: number;
  demand: number;
}

export interface OptimizationResult {
  optimization_id: string;
  timestamp: string;
  allocations: AllocationResult[];
  total_demand_gap_reduction: number;
  objective_value: number;
  solve_time_seconds: number;
  status: OptimizationStatus;
  input_data: OptimizationRequest;
  solver_used?: string;
  lp_formulation?: any;
  solution_details?: any;
}

export enum OptimizationStatus {
  OPTIMAL = 'optimal',
  INFEASIBLE = 'infeasible',
  UNBOUNDED = 'unbounded',
  ERROR = 'error',
  TIMEOUT = 'timeout'
}

export interface OptimizationResponse {
  result: OptimizationResult;
  message: string;
}

export interface SolverInfo {
  simple_available: boolean;
  advanced_available: boolean;
  default_solver: 'simple' | 'advanced';
  advanced_features: string[];
  simple_features: string[];
}

export interface SolverInfoResponse {
  solvers: SolverInfo;
  message: string;
}

// S&OP Types (re-export from sop.ts for convenience)
export * from './sop';

// Authentication Types (re-export from auth.ts for convenience)
export * from './auth';

// Context data interface for AI Assistant integration
export interface ContextData {
  filters: {
    active_tab: 'inventory' | 'demand' | 'gaps';
    tenant_id?: string;
    selected_nodes?: string[];
    selected_products?: string[];
    date_range?: {
      start: string;
      end: string;
    };
  };
  nodes: WarehouseNode[];
  products: Product[];
  inventory: InventoryLevel[];
  demand: DemandData[];
  demand_gaps: DemandGap[];
  summary: {
    total_inventory: number;
    total_demand: number;
    total_shortage: number;
    total_surplus: number;
    nodes_with_shortage: number;
    products_with_shortage: number;
  };
}